import React, { useEffect, useState } from "react";
import { api, API_BASE, getToken } from "@/lib/api";
import { Upload, Save, Image as ImageIcon, X } from "lucide-react";
import { toast } from "sonner";

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { api.get("/settings/payment").then((r) => setS(r.data)); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/payment", s);
      toast.success("Settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const uploadQr = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const token = getToken();
      const res = await fetch(`${API_BASE}/uploads/image`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
        body: fd,
      });
      if (!res.ok) throw new Error("Upload failed");
      const data = await res.json();
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setS((prev) => ({ ...prev, upi_qr_url: fullUrl }));
      toast.success("QR uploaded");
    } catch (e) { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  if (!s) return <div className="text-kyro-mute">Loading...</div>;

  return (
    <div className="max-w-3xl">
      <div className="kyro-overline">Payments</div>
      <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter mt-2 mb-2">UPI settings</h1>
      <p className="text-kyro-mute mb-8 text-sm">Customers will scan this QR / send to this UPI ID at checkout. Orders enter <strong className="text-kyro-ink">Verifying</strong> once they submit their UTR — you approve them from the Orders page.</p>

      <div className="space-y-6">
        {/* QR */}
        <div className="kyro-card p-6">
          <div className="kyro-overline mb-4">UPI QR image</div>
          <div className="grid md:grid-cols-[220px_1fr] gap-5">
            <div className="relative">
              <div className="w-full aspect-square rounded-xl overflow-hidden bg-white border border-white/10 grid place-items-center">
                {s.upi_qr_url ? (
                  <img src={s.upi_qr_url} alt="QR" className="w-full h-full object-contain" data-testid="settings-qr-preview" />
                ) : (
                  <div className="text-center p-4 text-gray-500">
                    <ImageIcon className="w-6 h-6 mx-auto mb-2" />
                    <p className="text-xs">No QR uploaded</p>
                  </div>
                )}
              </div>
              {s.upi_qr_url && (
                <button onClick={() => setS({ ...s, upi_qr_url: "" })} className="absolute top-2 right-2 w-7 h-7 grid place-items-center rounded-full bg-black/70 hover:bg-red-500/80 text-white"><X className="w-3.5 h-3.5" /></button>
              )}
            </div>
            <div className="space-y-3">
              <label className="kyro-btn-primary text-sm cursor-pointer inline-flex items-center gap-2">
                <Upload className="w-4 h-4" /> {uploading ? "Uploading..." : "Upload QR image"}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadQr(e.target.files[0])} data-testid="settings-qr-upload" />
              </label>
              <p className="text-xs text-kyro-mute leading-relaxed">Upload the QR screenshot you get from your UPI app (GPay / PhonePe / Paytm / BHIM) or your bank's business QR. Recommended: square PNG or JPG.</p>
              <div>
                <label className="text-[11px] text-kyro-mute uppercase tracking-widest mb-1 block">Or paste QR image URL</label>
                <input value={s.upi_qr_url} onChange={(e) => setS({ ...s, upi_qr_url: e.target.value })} placeholder="https://..." className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 outline-none focus:border-[#FF4433] text-sm" />
              </div>
            </div>
          </div>
        </div>

        {/* UPI ID + payee + numbers */}
        <div className="kyro-card p-6">
          <div className="kyro-overline mb-4">Payment methods</div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-kyro-mute uppercase tracking-widest mb-1 block">UPI ID</label>
              <input data-testid="settings-upi-id" value={s.upi_id} onChange={(e) => setS({ ...s, upi_id: e.target.value })} placeholder="yourname@okhdfc" className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 outline-none focus:border-[#FF4433] font-mono" />
            </div>
            <div>
              <label className="text-[11px] text-kyro-mute uppercase tracking-widest mb-1 block">Payee name *</label>
              <input data-testid="settings-payee" value={s.payee_name} onChange={(e) => setS({ ...s, payee_name: e.target.value })} placeholder="BABA MEDIA" className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 outline-none focus:border-[#FF4433]" />
            </div>
            <div>
              <label className="text-[11px] text-kyro-mute uppercase tracking-widest mb-1 block">GPay number</label>
              <input data-testid="settings-gpay" value={s.gpay_number || ""} onChange={(e) => setS({ ...s, gpay_number: e.target.value })} placeholder="9924731282" className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 outline-none focus:border-[#FF4433] font-mono" />
            </div>
            <div>
              <label className="text-[11px] text-kyro-mute uppercase tracking-widest mb-1 block">PhonePe number</label>
              <input data-testid="settings-phonepe" value={s.phonepe_number || ""} onChange={(e) => setS({ ...s, phonepe_number: e.target.value })} placeholder="9924731282" className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 outline-none focus:border-[#FF4433] font-mono" />
            </div>
            <div className="md:col-span-2">
              <label className="text-[11px] text-kyro-mute uppercase tracking-widest mb-1 block">WhatsApp number (with country code, no +)</label>
              <input data-testid="settings-whatsapp" value={s.whatsapp_number} onChange={(e) => setS({ ...s, whatsapp_number: e.target.value })} placeholder="919924731282" className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 outline-none focus:border-[#FF4433] font-mono" />
              <p className="text-xs text-kyro-faint mt-1">Shown on the order page so customers can WhatsApp you if they need help.</p>
            </div>
            <div className="md:col-span-2">
              <label className="text-[11px] text-kyro-mute uppercase tracking-widest mb-1 block">Instructions for customers</label>
              <textarea data-testid="settings-instructions" rows={3} value={s.instructions} onChange={(e) => setS({ ...s, instructions: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 outline-none focus:border-[#FF4433] text-sm" />
            </div>
            <div className="md:col-span-2">
              <label className="text-[11px] text-red-400 uppercase tracking-widest mb-1 block">⚠️ Warning message (shown on payment page)</label>
              <textarea data-testid="settings-warning" rows={3} value={s.warning_text || ""} onChange={(e) => setS({ ...s, warning_text: e.target.value })} className="w-full bg-red-500/5 border border-red-500/30 rounded-lg px-4 py-2.5 outline-none focus:border-red-500 text-sm" />
              <p className="text-xs text-kyro-faint mt-1">Discourage false "I've paid" claims. Blank = no warning shown.</p>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button onClick={save} disabled={saving} className="kyro-btn-primary text-sm inline-flex items-center gap-2" data-testid="save-settings-btn">
            <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save settings"}
          </button>
        </div>
      </div>
    </div>
  );
}
